import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, Send, UserRound, CalendarPlus } from 'lucide-react';
import { TO_API } from '../TimeOff';
import { RequestRow } from './MyTimeOff';
import BookForSomeone from './BookForSomeone';

export default function RequestsQueue({ me, user, onChanged }) {
  const [reqs, setReqs] = useState([]);
  const [managers, setManagers] = useState([]);
  const [filter, setFilter] = useState('open');
  const [acting, setActing] = useState(null); // { id, mode: 'review'|'approve'|'deny' }
  const [note, setNote] = useState('');
  const [managerId, setManagerId] = useState('');
  const [booking, setBooking] = useState(false);

  const load = async () => {
    try {
      const status = filter === 'open' ? 'submitted,hr_reviewed' : filter === 'all' ? undefined : filter;
      const r = await axios.get(`${TO_API}/requests`, { params: { status } });
      setReqs(r.data.requests || []);
    } catch { toast.error('Could not load requests'); }
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { axios.get(`${TO_API}/managers`).then(r => setManagers(r.data.managers || [])).catch(() => {}); }, []);

  const act = async () => {
    if (!acting) return;
    const path = acting.mode === 'review' ? 'hr-review' : acting.mode;
    try {
      await axios.post(`${TO_API}/requests/${acting.id}/${path}`, acting.mode === 'review' ? { note, manager_id: managerId } : { note });
      toast.success(acting.mode === 'review' ? 'Sent to the manager for approval' : acting.mode === 'approve' ? 'Approved — employee emailed' : 'Denied — employee emailed');
      setActing(null); setNote(''); setManagerId(''); load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const cancelReq = async (r) => {
    const why = window.prompt(`Cancel ${r.employee_name}'s ${r.type} (${r.date_from} → ${r.date_to})? Booked days are removed. Reason (optional):`);
    if (why === null) return;
    try { await axios.post(`${TO_API}/requests/${r.id}/cancel`, { note: why }); toast.success('Cancelled'); load(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const mine = (r) => r.manager_id === user?.id;
  return (
    <div className="space-y-3">
      <div className="flex gap-1 flex-wrap items-center">
        {[['open', 'Open'], ['approved', 'Approved'], ['denied', 'Denied'], ['cancelled', 'Cancelled'], ['all', 'All']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === v ? 'bg-white text-slate-900' : 'bg-slate-900/5 text-slate-800'}`} data-testid={`to-queue-filter-${v}`}>{l}</button>
        ))}
        <button onClick={() => setBooking(true)} className="ml-auto px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5" data-testid="to-book-for-someone-btn">
          <CalendarPlus className="w-4 h-4" /> Enter time off for someone
        </button>
      </div>
      {booking && <BookForSomeone me={me} onClose={() => setBooking(false)} onDone={() => { load(); onChanged(); }} />}
      {reqs.length === 0 && <p className="text-slate-700 text-sm">Nothing here.</p>}
      {reqs.map(r => (
        <div key={r.id}>
          <RequestRow r={{ ...r, note: r.note }}>
            <span className="w-full sm:w-auto text-xs text-slate-700 font-semibold self-center sm:mr-2 inline-flex items-center gap-1" data-testid={`to-req-emp-${r.id}`}><UserRound className="w-3.5 h-3.5 text-slate-500" />{r.employee_name}</span>
            {r.status === 'submitted' && me.is_hr && (
              <button onClick={() => { setActing({ id: r.id, mode: 'review' }); setNote(''); setManagerId(r.manager_id || ''); }} className="px-2.5 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium inline-flex items-center gap-1" data-testid={`to-review-${r.id}`}><Send className="w-3.5 h-3.5" /> HR review → manager</button>
            )}
            {['submitted', 'hr_reviewed'].includes(r.status) && me.is_manager && (r.status === 'hr_reviewed' || user?.role === 'super_admin') && (
              <button onClick={() => { setActing({ id: r.id, mode: 'approve' }); setNote(''); }} className={`px-2.5 py-1.5 rounded-lg text-white text-xs font-medium inline-flex items-center gap-1 ${mine(r) ? 'bg-emerald-600' : 'bg-emerald-500'}`} data-testid={`to-approve-${r.id}`}><Check className="w-3.5 h-3.5" /> Approve</button>
            )}
            {['submitted', 'hr_reviewed'].includes(r.status) && (me.is_manager || me.is_hr) && (
              <button onClick={() => { setActing({ id: r.id, mode: 'deny' }); setNote(''); }} className="px-2.5 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-medium inline-flex items-center gap-1" data-testid={`to-deny-${r.id}`}><X className="w-3.5 h-3.5" /> Deny</button>
            )}
            {r.status === 'approved' && me.is_hr && (
              <button onClick={() => cancelReq(r)} className="px-2.5 py-1.5 rounded-lg bg-slate-200 text-slate-700 text-xs font-medium" data-testid={`to-hrcancel-${r.id}`}>Cancel</button>
            )}
          </RequestRow>
          {acting?.id === r.id && (
            <div className="mt-1 sm:ml-4 bg-slate-50 border border-slate-200 rounded-lg p-3 flex flex-col md:flex-row gap-2 md:items-end" data-testid="to-action-panel">
              {acting.mode === 'review' && (
                <label className="text-xs text-slate-600">Manager to approve
                  <select value={managerId} onChange={e => setManagerId(e.target.value)} className="mt-1 block border border-slate-300 rounded-lg px-2 py-2 text-sm min-w-[200px]" data-testid="to-review-manager">
                    <option value="">— pick —</option>
                    {managers.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                  </select>
                </label>
              )}
              <label className="text-xs text-slate-600 flex-1">Note {acting.mode === 'review' ? 'for the manager' : 'to the employee'} (optional)
                <input value={note} onChange={e => setNote(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-2 py-2 text-sm" data-testid="to-action-note" />
              </label>
              <button onClick={act} disabled={acting.mode === 'review' && !managerId} className="px-4 py-2 rounded-lg bg-white text-slate-900 text-sm font-medium disabled:opacity-50" data-testid="to-action-confirm">
                {acting.mode === 'review' ? 'Send to manager' : acting.mode === 'approve' ? 'Approve & email' : 'Deny & email'}
              </button>
              <button onClick={() => setActing(null)} className="px-3 py-2 text-sm text-slate-600">Cancel</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
